import { Injectable, Injector } from '@angular/core';
import { Metadata } from '../model/login-details.model';
import { COUNTRY_AND_STATES_URL } from '../constants/login.constants';
import { AwsCredentialsData } from '../interfaces/login-details.interface';
import { LocalstorageService } from 'src/app/shared/providers/localstorage.service';
import { GetSiteIdsResponse } from 'src/app/modules/configurations/interfaces/configurations.interface';
import { CurrencyFormat, DrawingBalances, TierConfigurations } from 'src/app/modules/dynamic-content/interfaces/dynamic-content.interface';
import { ContentsService } from 'src/app/shared/services/contents.service';

@Injectable({
  providedIn: 'root'
})
export class LoginDetailsService {

  private awsCredentialsRefreshPromise: Promise<void> | null = null;

  constructor(private localStorageService: LocalstorageService,
    private injector: Injector
  ) {}

  setEndpointConfigurations(baseUrl: string) {
    this.localStorageService.saveData('baseUrl', baseUrl);
  }

  setAwsSignatureAuthorizationConfig(awsCredentials: AwsCredentialsData) {
    this.localStorageService.saveData('awsCredentials', JSON.stringify(awsCredentials));
  }

  setPlayerAttributes(propertyId: string, clientId: string, transEmployee: string, transEmployeeRole: string, transLocation: string) {
    this.localStorageService.saveData('propertyId', propertyId);
    this.localStorageService.saveData('clientId', clientId);
    this.localStorageService.saveData('transEmployee', transEmployee);
    this.localStorageService.saveData('transEmployeeRole', transEmployeeRole);
    this.localStorageService.saveData('transLocation', transLocation);
  }

  setPlayerId(playerId: string) {
    this.localStorageService.saveData('playerId', playerId);
  }

  setPlayerClubId(clubId: string) {
    this.localStorageService.saveData('playerClubId', clubId);
  }

  setPlayerUsername(username: string) {
    this.localStorageService.saveData('userName', username);
    if (username && username.includes('@')) {
      this.localStorageService.saveData('EMAIL', username);
      this.localStorageService.saveData('mail', username);
    }
  }

  setTenantCode(code: string) {
    this.localStorageService.saveData('tenantCode', code);
  }

  setPlayerBalanceAccountMap(accountIds: any) {
    this.localStorageService.saveData('playerBalanceAccountMap', JSON.stringify(accountIds));
  }

  setDefaultPlayerLocation(defaultPlayerLocation: any){
    this.localStorageService.saveData('defaultPlayerLocation', JSON.stringify(defaultPlayerLocation));
  }

  setAllCountriesAndStatesDataUrl(url: string) {
    if (!url) {
      url = COUNTRY_AND_STATES_URL;
    }
    this.localStorageService.saveData('countryAndStatesUrl', url);
  }

  setInitialContentCategoryName(contentCategoryName: string) {
    this.localStorageService.saveData('initialContentCategoryName', contentCategoryName);
  }

  setPlayerAccountConfigurations(playerAccountConfigurations: any) {
    this.localStorageService.saveData('playerAccountConfigurations', JSON.stringify(playerAccountConfigurations));
  }

  setCMSOfferTypes(cmsOfferTypes: any) {
    this.localStorageService.saveData('cmsOfferTypes', JSON.stringify(cmsOfferTypes));
  }

  setOfferConfiguration(offerConfigration: any) {
    this.localStorageService.saveData('offerConfigration', JSON.stringify(offerConfigration));
  }

  setInitialContentCategoryKey(contentCategoryKey: string) {
    this.localStorageService.saveData('initialContentCategoryKey', contentCategoryKey);
  }

  setAppThemingStyle(appThemingStyle: string) {
    this.localStorageService.saveData('appThemingStyle', appThemingStyle);
  }

  setMVStyle(mvStyle: any) {
    this.localStorageService.saveData('mvStyle', JSON.stringify(mvStyle));
  }

  setTierLevelBenefits(tierLevelBenefits: any) {
    this.localStorageService.saveData('tierLevelBenefits', JSON.stringify(tierLevelBenefits));
  }

  setMenuItems(menuItems: any) {
    this.localStorageService.saveData('menuItems', JSON.stringify(menuItems));
  }

  setMoreMenuItems(moreMenuItems: any) {
    this.localStorageService.saveData('moreMenuItems', JSON.stringify(moreMenuItems));
  }

  setTermsUrl(termsUrl: string) {
    this.localStorageService.saveData('termsUrl', termsUrl);
  }

  setPrivacyPolicyUrl(privacyPolicyUrl: string) {
    this.localStorageService.saveData('privacyPolicyUrl', privacyPolicyUrl);
  }

  setHelpUrl(helpUrl: string) {
    this.localStorageService.saveData('helpUrl', helpUrl);
  }

  setAccessibilityUrl(accessibilityUrl: string) {
    this.localStorageService.saveData('accessibilityUrl', accessibilityUrl);
  }

  setDesktopBgImage(desktopBgImage: string) {
    this.localStorageService.saveData('desktopBgImage', desktopBgImage);
  }

  setSecondaryButtonStyle(secondaryButtonStyle: string) {
    this.localStorageService.saveData('secondaryButtonStyle', secondaryButtonStyle);
  }

  setPrimaryButtonStyle(primaryButtonStyle: string) {
    this.localStorageService.saveData('primaryButtonStyle', primaryButtonStyle);
  }

  setAuxiliary1TextColor(auxiliary1TextColor: string) {
    this.localStorageService.saveData('auxiliary1TextColor', auxiliary1TextColor);
  }

  setTabletBgImage(tabletBgImage: string) {
    this.localStorageService.saveData('tabletBgImage', tabletBgImage);
  }

  setMobileBgImage(mobileBgImage: string) {
    this.localStorageService.saveData('mobileBgImage', mobileBgImage);
  }

  setSelectedSectionColor(selectedSectionColor: string) {
    this.localStorageService.saveData('selectedSectionColor', selectedSectionColor);
  }

  setUnselectedSectionColor(unselectedSectionColor: string) {
    this.localStorageService.saveData('unselectedSectionColor', unselectedSectionColor);
  }

  setIdentTypeArray(identTypeArray: any) {
    this.localStorageService.saveData('identTypeArray', JSON.stringify(identTypeArray));
  }

  setAddressType(addressType: string) {
    this.localStorageService.saveData('addressType', addressType);
  }

  setEmailType(emailType: string) {
    this.localStorageService.saveData('emailType', emailType);
  }

  setAppPlayerManagementSystemAttributes(appPlayerManagementSystemAttributes: any) {
    this.localStorageService.saveData('appPlayerManagementSystemAttributes', JSON.stringify(appPlayerManagementSystemAttributes));
  }

  setPhoneType(phoneType: string) {
    this.localStorageService.saveData('phoneType', phoneType);
  }

  setHeaderImage(headerImage: string) {
    this.localStorageService.saveData('headerImage', headerImage);
  }

  setFavIcon(favIcon: string) {
    this.localStorageService.saveData('favIcon', favIcon);
  }

  setTitle(title: string) {
    this.localStorageService.saveData('title', title);
  }

  setSiteIds(siteIds: GetSiteIdsResponse[]) {
    this.localStorageService.saveData('siteIds', JSON.stringify(siteIds));
  }

  setOfferTypes(offerTypes: any[]) {
    this.localStorageService.saveData('offerTypes', JSON.stringify(offerTypes));
  }

  setClubMembershipForPlayers(clubMembershipForPlayers: any[]) {
    this.localStorageService.saveData('clubMembershipForPlayers', JSON.stringify(clubMembershipForPlayers));
  }

  setClubRules(clubRules: any) {
    this.localStorageService.saveData('clubRules', JSON.stringify(clubRules));
  }

  getMetaData(): Metadata {
    return {
      propertyId: this.propertyId,
      clientId: this.clientId,
      transEmployee: this.transEmployee,
      transEmployeeRole: this.transEmployeeRole,
      transLocation: this.transLocation
    };
  }

  async getAwsSignatureAuthorizationConfig(): Promise<AwsCredentialsData | null> {
    let awsCredentials = this.getStoredAwsCredentials();

    if (!awsCredentials || this.isAWSTokenExpired(awsCredentials.expiry)) {
      await this.refreshAwsCredentials();
      awsCredentials = this.getStoredAwsCredentials();
    }

    return awsCredentials;
  }

  private getStoredAwsCredentials(): AwsCredentialsData | null {
    return JSON.parse(this.localStorageService.getData('awsCredentials'));
  }

  private async refreshAwsCredentials(): Promise<void> {
    if (!this.awsCredentialsRefreshPromise) {
      this.awsCredentialsRefreshPromise = this.injector
        .get(ContentsService)
        .getAWSCredentialsData()
        .then(() => undefined)
        .finally(() => {
          this.awsCredentialsRefreshPromise = null;
        });
    }

    await this.awsCredentialsRefreshPromise;
  }

  private isAWSTokenExpired(validity: number): boolean {
    if (validity === null || validity <= 0) {
      return true;
    }

    const currentDateTime = Date.now();
    const expirationDate = new Date(validity * 1000);
    expirationDate.setMinutes(expirationDate.getMinutes() - 5);
    return currentDateTime > expirationDate.getTime();
  }

  getPlayerAccountConfigurations() {
    return JSON.parse(this.localStorageService.getData('playerAccountConfigurations'));
  }

  setAccountIdMapConfig(accountIdMap: any) {
    this.localStorageService.saveData('accountIdMapConfig', JSON.stringify(accountIdMap));
  }

  setSupportNewPlayerRegistration(supportNewPlayerRegistration: boolean) {
    this.localStorageService.saveData('supportNewPlayerRegistration', JSON.stringify(supportNewPlayerRegistration));
  }

  setPlayerRegistrationOptionalFields(playerRegistrationOptionalFields: boolean) {
    this.localStorageService.saveData('playerRegistrationOptionalFields', JSON.stringify(playerRegistrationOptionalFields));
  }

  setPlayerMinimumAgeLimit(playerMinimumAgeLimit: number) {
    this.localStorageService.saveData('playerMinimumAgeLimit', JSON.stringify(playerMinimumAgeLimit));
  }

  setSupportWinLossStatement(supportWinLossStatement: boolean) {
    this.localStorageService.saveData('supportWinLossStatement', JSON.stringify(supportWinLossStatement));
  }

  setSupportSocialGaimg(supportSocialGaimg: boolean) {
    this.localStorageService.saveData('supportSocialGaimg', JSON.stringify(supportSocialGaimg));
  }

  setSupportPromotionalMoney(supportPromotionalMoney: boolean) {
    this.localStorageService.saveData('supportPromotionalMoney', JSON.stringify(supportPromotionalMoney));
  }

  setSupportAccountDeletion(supportAccountDeletion: boolean) {
    this.localStorageService.saveData('supportAccountDeletion', JSON.stringify(supportAccountDeletion));
  }

  setDisableAccountEmailUnlinking(disableAccountEmailUnlinking: boolean) {
    this.localStorageService.saveData('disableAccountEmailUnlinking', JSON.stringify(disableAccountEmailUnlinking));
  }

  setSupportChangePassword(supportChangePassword: boolean) {
    this.localStorageService.saveData('supportChangePassword', JSON.stringify(supportChangePassword));
  }

  setSupportTOTPMfa(supportTOTPMfa: boolean) {
    this.localStorageService.saveData('supportTOTPMfa', JSON.stringify(supportTOTPMfa));
  }

  setShowMfaSetupOnRegistration(showMfaSetupOnRegistration: boolean) {
    this.localStorageService.saveData('showMfaSetupOnRegistration', JSON.stringify(showMfaSetupOnRegistration));
  }

  setSupportOTPValidationForPlayerLinking(supportOTPValidationForPlayerLinking: boolean) {
    this.localStorageService.saveData('supportOTPValidationForPlayerLinking', JSON.stringify(supportOTPValidationForPlayerLinking));
  }

  setSupportPinChange(supportPinChange: boolean) {
    this.localStorageService.saveData('supportPinChange', JSON.stringify(supportPinChange));
  }

  setSupportedCurrencyFormat(supportedCurrencyFormat: CurrencyFormat) {
    this.localStorageService.saveData('supportedCurrencyFormat', JSON.stringify(supportedCurrencyFormat));
  }
  
  setSupportedDateTimeFormat(supportedDateTimeFormat: string) {
    this.localStorageService.saveData('supportedDateTimeFormat', JSON.stringify(supportedDateTimeFormat));
  }

  setSupportedOfferDateTimeFormat(supportedOfferDateTimeFormat: string) {
    this.localStorageService.saveData('supportedOfferDateTimeFormat', JSON.stringify(supportedOfferDateTimeFormat));
  }

  setTierConfigration(tierConfiguration: TierConfigurations) {
    this.localStorageService.saveData('tierConfiguration', JSON.stringify(tierConfiguration));
  }

  setDrawingConfigs(drawingConfigs: DrawingBalances[]) {
    this.localStorageService.saveData('drawingConfigs', JSON.stringify(drawingConfigs));
  }

  setSupportDrawingBalances(supportDrawingBalances: boolean) {
    this.localStorageService.saveData('supportDrawingBalances', JSON.stringify(supportDrawingBalances));
  }

  setSupportAllowAccountInfoEdit(supportAllowAccountInfoEdit: boolean) {
    this.localStorageService.saveData('supportAllowAccountInfoEdit', JSON.stringify(supportAllowAccountInfoEdit));
  }

  setFetchDrawingBalancesUsingDirectApiCall(fetchDrawingBalancesUsingDirectApiCall: boolean) {
    this.localStorageService.saveData('fetchDrawingBalancesUsingDirectApiCall', JSON.stringify(fetchDrawingBalancesUsingDirectApiCall));
  }

  setIsIdentVerificationRequired(isIdentVerificationRequired: boolean) {
    this.localStorageService.saveData('isIdentVerificationRequired', JSON.stringify(isIdentVerificationRequired));
  }

  get isIdentVerificationRequired() {
    return JSON.parse(this.localStorageService.getData('isIdentVerificationRequired'));
  }
  
  get fetchDrawingBalancesUsingDirectApiCall() {
    return JSON.parse(this.localStorageService.getData('fetchDrawingBalancesUsingDirectApiCall'));
  }
  
  get supportDrawingBalances() {
    return JSON.parse(this.localStorageService.getData('supportDrawingBalances'));
  }
  
  get supportAllowAccountInfoEdit() {
    return JSON.parse(this.localStorageService.getData('supportAllowAccountInfoEdit'));
  }

  get drawingConfigs() {
    return JSON.parse(this.localStorageService.getData('drawingConfigs'));
  }
  
  get tierConfiguration() {
    return JSON.parse(this.localStorageService.getData('tierConfiguration'));
  }

  get supportedCurrencyFormat() {
    return JSON.parse(this.localStorageService.getData('supportedCurrencyFormat'));
  }

  get supportedDateTimeFormat() {
    return JSON.parse(this.localStorageService.getData('supportedDateTimeFormat'));
  }

  get supportedOfferDateTimeFormat() {
    return JSON.parse(this.localStorageService.getData('supportedOfferDateTimeFormat'));
  }

  get supportNewPlayerRegistration() {
    return JSON.parse(this.localStorageService.getData('supportNewPlayerRegistration'));
  }

  get playerRegistrationOptionalFields() {
    return JSON.parse(this.localStorageService.getData('playerRegistrationOptionalFields'));
  }

  get playerMinimumAgeLimit() {
    return JSON.parse(this.localStorageService.getData('playerMinimumAgeLimit'));
  }

  get supportWinLossStatement() {
    return JSON.parse(this.localStorageService.getData('supportWinLossStatement'));
  }

  get supportSocialGaimg() {
    return JSON.parse(this.localStorageService.getData('supportSocialGaimg'));
  }

  get supportPromotionalMoney() {
    return JSON.parse(this.localStorageService.getData('supportPromotionalMoney'));
  }

  get supportAccountDeletion() {
    return JSON.parse(this.localStorageService.getData('supportAccountDeletion'));
  }

  get disableAccountEmailUnlinking() {
    return JSON.parse(this.localStorageService.getData('disableAccountEmailUnlinking'));
  }

  get supportChangePassword() {
    return JSON.parse(this.localStorageService.getData('supportChangePassword'));
  }

  get supportTOTPMfa() {
    return JSON.parse(this.localStorageService.getData('supportTOTPMfa'));
  }

  get showMfaSetupOnRegistration() {
    return JSON.parse(this.localStorageService.getData('showMfaSetupOnRegistration'));
  }

  get supportOTPValidationForPlayerLinking() {
    const value = this.localStorageService.getData('supportOTPValidationForPlayerLinking');
    if (value === null || value === undefined || value === '') {
      return true;
    }
    return JSON.parse(value);
  }

  get supportPinChange() {
    return JSON.parse(this.localStorageService.getData('supportPinChange'));
  }
  
  get offerConfigration() {
    return JSON.parse(this.localStorageService.getData('offerConfigration'));
  }

  get baseUrl() {
    return this.localStorageService.getData('baseUrl');
  }

  get propertyId() {
    return this.localStorageService.getData('propertyId');
  }

  get clientId() {
    return this.localStorageService.getData('clientId');
  }

  get transEmployee() {
    return this.localStorageService.getData('transEmployee');
  }

  get transEmployeeRole() {
    return this.localStorageService.getData('transEmployeeRole');
  }

  get transLocation() {
    return this.localStorageService.getData('transLocation');
  }

  get playerId() {
    return this.localStorageService.getData('playerId');
  }

  get tenantCode() {
    return this.localStorageService.getData('tenantCode');
  }

  get userName() {
    return this.localStorageService.getData('userName');
  }

  get countryAndStatesUrl() {
    return this.localStorageService.getData('countryAndStatesUrl');
  }

  get playerBalanceAccountMap() {
    return JSON.parse(this.localStorageService.getData('playerBalanceAccountMap'));
  }

  get playerClubId() {
    return this.localStorageService.getData('playerClubId');
  }

  get cmsOfferTypes() {
    return JSON.parse(this.localStorageService.getData('cmsOfferTypes'));
  }

  get appThemingStyle() {
    return this.localStorageService.getData('appThemingStyle');
  }

  get mvStyle() {
    return JSON.parse(this.localStorageService.getData('mvStyle'));
  }

  get tierLevelBenefits() {
    return JSON.parse(this.localStorageService.getData('tierLevelBenefits'));
  }

  get menuItems() {
    return JSON.parse(this.localStorageService.getData('menuItems'));
  }

  get moreMenuItems() {
    return JSON.parse(this.localStorageService.getData('moreMenuItems'));
  }

  get termsUrl() {
    return this.localStorageService.getData('termsUrl');
  }

  get privacyPolicyUrl() {
    return this.localStorageService.getData('privacyPolicyUrl');
  }

  get helpUrl() {
    return this.localStorageService.getData('helpUrl');
  }

  get accessibilityUrl() {
    return this.localStorageService.getData('accessibilityUrl');
  }

  get desktopBgImage() {
    return this.localStorageService.getData('desktopBgImage');
  }

  get secondaryButtonStyle() {
    return this.localStorageService.getData('secondaryButtonStyle');
  }

  get primaryButtonStyle() {
    return this.localStorageService.getData('primaryButtonStyle');
  }

  get auxiliary1TextColor() {
    return this.localStorageService.getData('auxiliary1TextColor');
  }

  get tabletBgImage() {
    return this.localStorageService.getData('tabletBgImage');
  }

  get mobileBgImage() {
    return this.localStorageService.getData('mobileBgImage');
  }

  get selectedSectionColor() {
    return this.localStorageService.getData('selectedSectionColor');
  }

  get unselectedSectionColor() {
    return this.localStorageService.getData('unselectedSectionColor');
  }

  get identTypeArray() {
    return JSON.parse(this.localStorageService.getData('identTypeArray'));
  }

  get addressType() {
    return this.localStorageService.getData('addressType');
  }

  get emailType() {
    return this.localStorageService.getData('emailType');
  }

  get appPlayerManagementSystemAttributes() {
    return JSON.parse(this.localStorageService.getData('appPlayerManagementSystemAttributes'));
  }

  get phoneType() {
    return this.localStorageService.getData('phoneType');
  }

  get headerImage() {
    return this.localStorageService.getData('headerImage');
  }

  get favIcon() {
    return this.localStorageService.getData('favIcon');
  }

  get title() {
    return this.localStorageService.getData('title') || '...';
  }

  get initialContentCategoryKey() {
    return this.localStorageService.getData('initialContentCategoryKey');
  }

  get initialContentCategoryName() {
    return this.localStorageService.getData('initialContentCategoryName');
  }

  get getAccountIdMapConfig() {
    return JSON.parse(this.localStorageService.getData('accountIdMapConfig'));
  }

  get defaultPlayerLocation(){
    return JSON.parse(this.localStorageService.getData('defaultPlayerLocation'));
  }

  get siteIds() {
    return JSON.parse(this.localStorageService.getData('siteIds'));
  }

  get offerTypes() {
    return JSON.parse(this.localStorageService.getData('offerTypes'));
  }

  get clubMembershipForPlayers() {
    return JSON.parse(this.localStorageService.getData('clubMembershipForPlayers'));
  }

  get clubRules() {
    return JSON.parse(this.localStorageService.getData('clubRules'));
  }


  removeStorageData(key: string) {
    this.localStorageService.deleteData(key);
  }

  /**
   * Clear all user data from localStorage
   * Called during logout to ensure complete cleanup
   * Preserves configuration/branding data
   */
  public clearAllUserData(): void {
    console.log('[LoginDetailsService] Clearing all user data');
    
    // List of user-specific keys to clear
    const userDataKeys = [
      'playerId',
      'playerClubId',
      'userName',
      'EMAIL',
      'mail',
      'playerBalanceAccountMap',
      'defaultPlayerLocation',
      'initialContentCategoryName',
      'playerAccountConfigurations',
      'cmsOfferTypes',
      'offerConfigration',
      'initialContentCategoryKey',
      'awsCredentials',
      'guid_email'
    ];
    
    userDataKeys.forEach(key => {
      try {
        this.localStorageService.deleteData(key);
        console.log(`[LoginDetailsService] Cleared: ${key}`);
      } catch (error) {
        console.warn(`[LoginDetailsService] Error clearing ${key}:`, error);
      }
    });

    console.log('[LoginDetailsService] User data cleanup completed');
  }
  
}
